import React, { useState } from 'react';
import { Calculator, Copy, Trash2, TrendingUp } from 'lucide-react';

const CagrCalculatorTool = () => {
  const [beginningValue, setBeginningValue] = useState('');
  const [endingValue, setEndingValue] = useState('');
  const [years, setYears] = useState('');
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState('');

  const calculate = () => {
    const startValue = parseFloat(beginningValue);
    const endValue = parseFloat(endingValue);
    const numYears = parseFloat(years);

    if (isNaN(startValue) || isNaN(endValue) || isNaN(numYears) || 
        startValue <= 0 || endValue <= 0 || numYears <= 0) {
      setResult(null);
      return;
    }

    // CAGR = ((End / Start)^(1/years)) - 1
    const cagr = (Math.pow(endValue / startValue, 1 / numYears) - 1) * 100;
    const totalGrowth = ((endValue - startValue) / startValue) * 100;
    const absoluteGrowth = endValue - startValue;

    setResult({
      beginningValue: startValue,
      endingValue: endValue,
      years: numYears,
      cagr,
      totalGrowth,
      absoluteGrowth,
      isPositive: cagr >= 0
    });
  };

  const copyResult = (value, label) => {
    navigator.clipboard.writeText(typeof value === 'number' ? value.toFixed(2) : value);
    setCopied(label);
    setTimeout(() => setCopied(''), 2000);
  };

  const clearAll = () => {
    setBeginningValue('');
    setEndingValue('');
    setYears('');
    setResult(null);
  };

  const formatCurrency = (value) => {
    return '$' + value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const formatPercent = (value) => {
    const sign = value >= 0 ? '+' : '';
    return sign + value.toFixed(2) + '%';
  };

  return (
    <div className="space-y-6">
      {/* Input Section */}
      <div className="p-4 bg-gray-800 rounded-lg space-y-4">
        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Beginning Value ($)
            </label>
            <input
              type="number"
              step="0.01"
              value={beginningValue}
              onChange={(e) => setBeginningValue(e.target.value)}
              placeholder="e.g., 10000"
              className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none"
              data-testid="beginning-value-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Ending Value ($)
            </label>
            <input
              type="number"
              step="0.01"
              value={endingValue}
              onChange={(e) => setEndingValue(e.target.value)}
              placeholder="e.g., 15000"
              className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none"
              data-testid="ending-value-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Number of Years
            </label>
            <input
              type="number"
              step="0.1"
              value={years}
              onChange={(e) => setYears(e.target.value)}
              placeholder="e.g., 5"
              className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none"
              data-testid="years-input"
            />
          </div>
        </div>
      </div>

      {/* Calculate Button */}
      <button
        onClick={calculate}
        className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium"
        data-testid="calculate-cagr-btn"
      >
        <Calculator size={18} />
        Calculate CAGR
      </button>

      {/* Results Section */}
      {result && (
        <div className="p-4 bg-gray-800 rounded-lg space-y-4" data-testid="cagr-results">
          {/* Main Result */}
          <div className="p-4 bg-gray-900 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <TrendingUp size={18} className={result.isPositive ? 'text-green-400' : 'text-red-400'} />
                <span className="text-sm text-gray-400">Compound Annual Growth Rate (CAGR)</span>
              </div>
              <button
                onClick={() => copyResult(result.cagr, 'cagr')}
                className={`p-1.5 rounded transition-colors ${
                  copied === 'cagr' ? 'bg-green-600' : 'bg-gray-700 hover:bg-gray-600'
                }`}
                data-testid="copy-cagr"
              >
                <Copy size={14} />
              </button>
            </div>
            <div 
              className={`text-3xl font-bold ${result.isPositive ? 'text-green-400' : 'text-red-400'}`}
              data-testid="cagr-result"
            >
              {formatPercent(result.cagr)}
            </div>
            <div className="text-sm text-gray-400 mt-1">
              per year over {result.years} years
            </div>
          </div>

          {/* Additional Stats */}
          <div className="grid md:grid-cols-3 gap-4">
            <div className="p-4 bg-gray-900 rounded-lg">
              <div className="text-sm text-gray-400 mb-1">Beginning Value</div>
              <div className="text-xl font-bold text-blue-400">
                {formatCurrency(result.beginningValue)}
              </div>
            </div>
            <div className="p-4 bg-gray-900 rounded-lg">
              <div className="text-sm text-gray-400 mb-1">Ending Value</div>
              <div className="text-xl font-bold text-purple-400">
                {formatCurrency(result.endingValue)}
              </div>
            </div>
            <div className="p-4 bg-gray-900 rounded-lg">
              <div className="text-sm text-gray-400 mb-1">Total Growth</div>
              <div className={`text-xl font-bold ${result.isPositive ? 'text-green-400' : 'text-red-400'}`}>
                {formatPercent(result.totalGrowth)}
              </div>
            </div>
          </div>

          {/* Summary */}
          <div className="border-t border-gray-700 pt-4">
            <div className="flex items-center gap-2 mb-3">
              <Calculator size={16} className="text-blue-400" />
              <h4 className="text-sm font-medium text-gray-300">Calculation Summary</h4>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Beginning Value</span>
                <span className="text-blue-400 font-medium">{formatCurrency(result.beginningValue)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Ending Value</span>
                <span className="text-purple-400 font-medium">{formatCurrency(result.endingValue)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Time Period</span>
                <span className="text-white font-medium">{result.years} years</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Absolute Growth</span>
                <span className={`font-medium ${result.isPositive ? 'text-green-400' : 'text-red-400'}`}>
                  {result.absoluteGrowth >= 0 ? '+' : ''}{formatCurrency(result.absoluteGrowth)}
                </span>
              </div>
              <div className="flex justify-between text-sm border-t border-gray-700 pt-2 mt-2">
                <span className="text-gray-400">CAGR</span>
                <span className={`font-medium ${result.isPositive ? 'text-green-400' : 'text-red-400'}`}>
                  {formatPercent(result.cagr)}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Formula Section */}
      <div className="p-4 bg-gray-800/50 rounded-lg">
        <h3 className="text-sm font-medium text-gray-300 mb-2">Formula</h3>
        <div className="text-sm text-gray-400 font-mono bg-gray-900 p-3 rounded">
          CAGR = ((Ending Value / Beginning Value)^(1/Years)) - 1
        </div>
      </div>

      <div className="p-4 bg-gray-800/50 rounded-lg">
        <h3 className="text-sm font-medium text-gray-300 mb-2">What is CAGR?</h3>
        <div className="text-sm text-gray-400">
          CAGR (Compound Annual Growth Rate) measures the mean annual growth rate of an investment over a specified time period longer than one year. It represents the smoothed rate at which an investment would have grown if it had grown at the same rate every year.
        </div>
      </div>

      {/* Clear Button */}
      <button
        onClick={clearAll}
        className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
        data-testid="clear-cagr-btn"
      >
        <Trash2 size={16} />
        Clear All
      </button>
    </div>
  );
};

export default CagrCalculatorTool;
